/**
 * Native UI configuration — status bar, keyboard, orientation.
 * Called once during app boot on native platforms.
 */

import { isNative, isIos, isAndroid } from './platform';

export async function configureNativeUI(): Promise<void> {
  if (!isNative()) return;

  // Status bar: dark style (white text) on dark background
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setOverlaysWebView({ overlay: true });
    // Allow the WebView a frame to propagate safe-area-inset values
    await new Promise(r => requestAnimationFrame(r));
  } catch {
    // StatusBar plugin may not be available on all platforms
  }

  // Keyboard resize mode:
  // Android: use 'none' — the app handles keyboard offset via visualViewport
  // (useVisualViewportBottomInset) and applies it as style={{ bottom }} on ChatWrapper.
  // iOS: keep 'body' since iOS WebView + contentInset: 'automatic' handles it well.
  try {
    const { Keyboard } = await import('@capacitor/keyboard');
    // Both platforms: 'none' — app handles keyboard offset via --keyboard-height CSS
    // variable (iOS) and visualViewport API (Android). contentInset: 'never' prevents
    // native safe area padding that causes unwanted lines at top/bottom.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await Keyboard.setResizeMode({ mode: 'none' } as any);
    await Keyboard.setScroll({ isDisabled: false });
    // iOS: show "Done" button above keyboard for dismissing
    if (isIos()) {
      await Keyboard.setAccessoryBarVisible({ isVisible: true });

      // Track keyboard height as CSS variable. All position:fixed elements
      // that need to avoid the keyboard use bottom: var(--keyboard-height, 0px).
      //
      // We animate --keyboard-height via rAF to stay in sync with the native
      // iOS keyboard animation (~280ms, ease-out cubic).  No CSS transition
      // is needed on consuming elements.
      let kbAnimId = 0;

      /** Find the nearest scrollable ancestor of an element */
      const findScroller = (el: HTMLElement): HTMLElement | null => {
        let node: HTMLElement | null = el.parentElement;
        while (node) {
          const { overflowY } = getComputedStyle(node);
          if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
            return node;
          }
          node = node.parentElement;
        }
        return null;
      };

      /** Scroll focused input to centre of visible area above keyboard */
      const scrollInputIntoView = (kbHeight: number) => {
        const el = document.activeElement;
        if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement ||
              (el instanceof HTMLElement && el.getAttribute('contenteditable') === 'true'))) return;
        const scroller = findScroller(el);
        if (!scroller) return;
        const scrollerRect = scroller.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const visibleHeight = scrollerRect.height - kbHeight;
        const elCenter = elRect.top + elRect.height / 2 - scrollerRect.top + scroller.scrollTop;
        const targetScroll = elCenter - visibleHeight / 2;
        scroller.scrollTo({ top: Math.max(0, targetScroll), behavior: 'smooth' });
      };

      const animateKeyboardHeight = (from: number, to: number) => {
        cancelAnimationFrame(kbAnimId);
        const duration = 280;
        const start = performance.now();

        const step = (now: number) => {
          const t = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - t, 3);
          const current = from + (to - from) * eased;
          document.documentElement.style.setProperty('--keyboard-height', `${current}px`);

          if (t < 1) {
            kbAnimId = requestAnimationFrame(step);
          }
        };
        kbAnimId = requestAnimationFrame(step);
      };

      await Keyboard.addListener('keyboardWillShow', (info) => {
        console.log('[Keyboard] willShow height:', info.keyboardHeight);
        const current = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--keyboard-height')
        ) || 0;
        // Set final target immediately so padding-bottom grows instantly
        // (the padded area is behind the keyboard — the jump is invisible).
        document.documentElement.style.setProperty('--keyboard-height-final', `${info.keyboardHeight}px`);
        // Force layout reflow so padding-bottom is applied before we measure.
        // Without this the first-ever keyboard show fails because scrollHeight
        // hasn't grown yet and findScroller returns null.
        void document.documentElement.offsetHeight;
        animateKeyboardHeight(current, info.keyboardHeight);
        scrollInputIntoView(info.keyboardHeight);
      });
      await Keyboard.addListener('keyboardWillHide', () => {
        console.log('[Keyboard] willHide');
        const current = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--keyboard-height')
        ) || 0;
        document.documentElement.style.setProperty('--keyboard-height-final', '0px');
        animateKeyboardHeight(current, 0);
      });
    }
  } catch {
    // Keyboard plugin may not be available
  }

  // Android: track keyboard height via visualViewport and expose as --keyboard-height
  // (iOS is handled above via Capacitor Keyboard plugin events)
  if (isAndroid()) {
    const vv = window.visualViewport;
    if (vv) {
      const measure = () => {
        const layoutH = window.innerHeight;
        const vvH = vv.height ?? layoutH;
        const offsetTop = vv.offsetTop ?? 0;
        const inset = Math.max(0, Math.round(layoutH - vvH - offsetTop));
        document.documentElement.style.setProperty('--keyboard-height', `${inset}px`);
      };
      vv.addEventListener('resize', measure);
      vv.addEventListener('scroll', measure);
      document.addEventListener('focusin', measure);
      document.addEventListener('focusout', () => {
        // Small delay to let viewport settle after blur
        setTimeout(() => {
          document.documentElement.style.setProperty('--keyboard-height', '0px');
        }, 100);
      });
    }
  }

  // Reset viewport when app resumes from background.
  // Hide keyboard (if open) and dispatch a resize event so visualViewport
  // listeners re-measure. We intentionally avoid setting body.style.height
  // to '100vh' which is unstable on Android (includes nav bar) and causes
  // layout shifts with position:fixed elements.
  try {
    const { App } = await import('@capacitor/app');
    const { Keyboard } = await import('@capacitor/keyboard');
    await App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        try { await Keyboard.hide(); } catch { /* may not be showing */ }
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, 100);
      }
    });
  } catch {
    // App/Keyboard plugin may not be available
  }
}
